/**
 * Merchant Generation Hint Generator
 * Provides humorous, mad-libs style hints for merchant generation progress
 * Themed around discovering and visiting a merchant's shop
 */

const sections = [
    {
        id: 0,
        titles: [
            "Hearing Rumors",
            "Asking Around Town",
            "Following a Lead",
            "Tavern Gossip",
            "Word of Mouth"
        ],
        progressRange: [0, 20],
        hints: [
            "A {patron} at the tavern mentions a {adjective} merchant in the {district}...",
            "Following a rumor about someone selling {adjective} {merchandise} nearby...",
            "A {patron} swears they got a {adjective} deal on a {merchandise} last week...",
            "Overhearing a {patron} haggling over the price of {adjective} {merchandise}...",
            "Deciphering a {adjective} flyer pinned to the notice board...",
            "A street {patron} offers directions to the {adjective} {shopType} for a copper...",
            "Asking the {patron} if they know anyone selling {adjective} {merchandise}...",
            "The innkeeper mentions a {adjective} trader who recently set up in the {district}...",
            "A {patron} whispers about a {adjective} {shopType} that only opens at {time}...",
            "Piecing together conflicting directions to the {adjective} {shopType}...",
            "A notice reads: '{adjective} {merchandise} — fair prices — ask for {name}'...",
            "The town crier announces a new {adjective} {shopType} in the {district}...",
            "A {patron} warns that {name}'s prices are {adjective} but the {merchandise} is worth it...",
            "Checking the merchant's guild registry for a {adjective} {shopType}...",
            "Following the scent of {adjective} {merchandise} through the {district}..."
        ]
    },
    {
        id: 1,
        titles: [
            "Finding the Shop",
            "Down the Alley",
            "The Storefront",
            "Navigating the Market",
            "Spotting the Sign"
        ],
        progressRange: [20, 40],
        hints: [
            "Spotting a {adjective} sign that reads '{name}'s {shopType}'...",
            "Navigating through the market stalls to reach the {adjective} {shopType}...",
            "The {shopType} is nestled between a {adjective} bakery and a {adjective} smithy...",
            "A {adjective} {decoration} hangs above the entrance to the {shopType}...",
            "Peering through the {adjective} window at shelves of {merchandise}...",
            "The door to the {shopType} creaks with a {adjective} groan...",
            "A {adjective} cat watches from the windowsill of the {shopType}...",
            "Stepping over crates of {merchandise} blocking the entrance...",
            "The {shopType} smells of {adjective} {merchandise} and old parchment...",
            "A bell with a {adjective} chime announces your arrival...",
            "Noticing a {adjective} 'Open' sign written in several languages...",
            "The {district} {shopType} looks more {adjective} than expected...",
            "Squeezing past a {patron} hauling {adjective} {merchandise} out the door...",
            "The floorboards creak under shelves packed with {adjective} {merchandise}...",
            "A {adjective} parrot on a perch squawks prices at passersby..."
        ]
    },
    {
        id: 2,
        titles: [
            "Meeting the Merchant",
            "First Impressions",
            "The Introduction",
            "Behind the Counter",
            "The Proprietor"
        ],
        progressRange: [40, 60],
        hints: [
            "A {adjective} figure emerges from behind towers of {merchandise}...",
            "The merchant adjusts their {adjective} {clothing} and offers a {adjective} smile...",
            "They introduce themselves with a {adjective} bow and a flourish of their {clothing}...",
            "'{greeting}!' they exclaim, gesturing at shelves of {merchandise}...",
            "The merchant slides a {adjective} {merchandise} across the counter as a sample...",
            "Their {adjective} eyes appraise you while polishing a {merchandise}...",
            "A {adjective} handshake seals the introduction—their grip is surprisingly firm...",
            "They offer a cup of {adjective} tea before discussing {merchandise}...",
            "The merchant's {adjective} {clothing} jingles with hidden pockets...",
            "They claim to have decades of experience in {adjective} {merchandise}...",
            "A {adjective} ledger the size of a shield sits open on the counter...",
            "The merchant whistles a {adjective} tune while arranging {merchandise}...",
            "'{greeting}, traveler! Looking for something {adjective}?' they ask...",
            "They produce a {adjective} business card from inside their {clothing}...",
            "The merchant's {adjective} reputation apparently precedes them..."
        ]
    },
    {
        id: 3,
        titles: [
            "Browsing the Wares",
            "Inspecting Inventory",
            "Taking Stock",
            "The Collection",
            "Examining the Goods"
        ],
        progressRange: [60, 80],
        hints: [
            "Examining a {adjective} {merchandise} with unusual properties...",
            "The merchant produces a {adjective} {merchandise} from a locked display...",
            "Testing the weight of a {adjective} {merchandise}—heavier than it looks...",
            "Browsing racks of {adjective} {merchandise} organized by quality...",
            "The merchant insists this {adjective} {merchandise} was sourced from a {origin}...",
            "A {adjective} {merchandise} catches the light from the shop window...",
            "Opening a case lined with {adjective} velvet, revealing rare {merchandise}...",
            "The merchant unrolls a {adjective} inventory list that reaches the floor...",
            "Comparing different {adjective} {merchandise}s side by side...",
            "A hidden compartment reveals the merchant's {adjective} premium stock...",
            "The {adjective} craftsmanship on this {merchandise} is unmistakable...",
            "The merchant claims this {merchandise} was blessed by a {adjective} {patron}...",
            "Turning a {adjective} {merchandise} over to inspect the maker's mark...",
            "The back room holds even more shelves of {adjective} {merchandise}...",
            "A {adjective} price tag dangling from the {merchandise} seems negotiable..."
        ]
    },
    {
        id: 4,
        titles: [
            "Closing the Deal",
            "The Negotiation",
            "Striking a Bargain",
            "Final Offer",
            "Sealing the Trade"
        ],
        progressRange: [80, 100],
        hints: [
            "Haggling over the price of a {adjective} {merchandise}—seems fair enough...",
            "The merchant throws in a {adjective} {merchandise} to sweeten the deal...",
            "Shaking hands on a {adjective} bargain both parties can live with...",
            "The merchant wraps the {merchandise} in {adjective} cloth with surprising care...",
            "Counting out coins while the merchant tallies the {adjective} receipt...",
            "'{greeting}! Come back anytime,' the merchant says with a {adjective} wink...",
            "The merchant stamps a {adjective} loyalty card—almost a free {merchandise}...",
            "Tucking the {adjective} {merchandise} safely into a travel pack...",
            "The merchant slips a {adjective} business card into the bag...",
            "A {adjective} handshake and a promise of future discounts...",
            "The merchant is already rearranging the {adjective} display for the next customer...",
            "Walking away lighter in gold but with a {adjective} {merchandise} in hand...",
            "The merchant waves from the doorway of the {adjective} {shopType}...",
            "Making a mental note to return when the {adjective} shipment arrives...",
            "The {adjective} receipt reads: 'All sales final. Probably.'"
        ]
    }
];

const wordBanks = {
    adjective: ['peculiar', 'enchanted', 'dusty', 'gleaming', 'weathered', 'ornate', 'mysterious', 'well-crafted', 'ancient', 'exquisite', 'suspiciously cheap', 'hand-stitched', 'gilded', 'battered', 'polished', 'imported', 'rustic', 'aromatic'],
    merchandise: ['potion', 'amulet', 'blade', 'scroll', 'trinket', 'compass', 'gemstone', 'spice', 'tonic', 'relic', 'candle', 'map', 'charm', 'elixir', 'lantern', 'lockpick set'],
    shopType: ['Emporium', 'Trading Post', 'Curiosity Shop', 'Supply Store', 'Market Stall', 'Bazaar', 'General Store', 'Apothecary', 'Outfitter', 'Provisions Shop'],
    district: ['market square', 'dockside', 'lower quarter', 'old town', 'merchant row', 'harbor district', 'artisan ward', 'crossroads', 'guild quarter', 'bazaar alley'],
    patron: ['dwarf', 'sailor', 'bard', 'guardsman', 'farmer', 'traveler', 'pilgrim', 'adventurer', 'retired knight', 'wandering monk'],
    clothing: ['apron', 'vest', 'cloak', 'hat', 'scarf', 'sash', 'gloves', 'overcoat', 'bandana', 'spectacles'],
    decoration: ['lantern', 'wind chime', 'banner', 'wreath', 'sign', 'garland', 'birdcage', 'flag', 'tapestry', 'mobile'],
    greeting: ['Welcome', 'Ah, a customer', 'Well met', 'Step right in', 'Good timing', 'At last', 'Greetings, friend', 'Come in, come in'],
    origin: ['dwarven forge', 'distant kingdom', 'sunken ship', 'forgotten temple', 'mountain monastery', 'desert caravan', 'elven workshop', 'pirate auction'],
    time: ['dusk', 'midnight', 'dawn', 'market hours', 'the full moon', 'high noon', 'the third bell', 'sunset'],
    name: ['Barrick', 'Yenna', 'Tormand', 'Sable', 'Grimbold', 'Whistle', 'Copperkettle', 'Dusthaven', 'Jinx', 'Bramblewick', 'Fernsby', 'Ashwick', 'Talloway', 'Coppergrin', 'Quillsby', 'Kettleworth']
};

function getRandomItem(array) {
    return array[Math.floor(Math.random() * array.length)];
}

function fillHintTemplate(template) {
    return template.replace(/\{(\w+)\}/g, (match, wordType) => {
        if (wordBanks[wordType]) {
            return getRandomItem(wordBanks[wordType]);
        }
        return match;
    });
}

function getRandomTitle(titles) {
    return getRandomItem(titles);
}

export function initializeSectionTitles() {
    return sections.map(section => ({
        ...section,
        selectedTitle: getRandomTitle(section.titles)
    }));
}

export function getSectionForProgress(progress, sectionsWithTitles = null) {
    const sourceSections = sectionsWithTitles || sections;
    const section = sourceSections.find(s =>
        progress >= s.progressRange[0] && progress < s.progressRange[1]
    ) || sourceSections[sourceSections.length - 1];

    return section;
}

export function getHintForProgress(progress, sectionsWithTitles = null) {
    const section = getSectionForProgress(progress, sectionsWithTitles);
    const template = getRandomItem(section.hints);
    return fillHintTemplate(template);
}

export function getAllSections() {
    return sections;
}

export function getSectionProgress(progress, sectionsWithTitles = null) {
    const section = getSectionForProgress(progress, sectionsWithTitles);
    const sectionStart = section.progressRange[0];
    const sectionEnd = section.progressRange[1];
    const sectionSpan = sectionEnd - sectionStart;
    const progressInSection = progress - sectionStart;
    return (progressInSection / sectionSpan) * 100;
}

export default {
    getHintForProgress,
    getSectionForProgress,
    getAllSections,
    getSectionProgress,
    initializeSectionTitles
};
